import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface Error {
  id: string;
  message: string;
  timestamp: number;
  source?: string;
  isRead: boolean;
}

interface ErrorContextType {
  errors: Error[];
  addError: (message: string, source?: string) => void;
  markAsRead: (id: string) => void;
  removeError: (id: string) => void;
  clearAllErrors: () => void;
  unreadCount: number;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const useErrorContext = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useErrorContext must be used within an ErrorProvider');
  }
  return context;
};

interface ErrorProviderProps {
  children: ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const [errors, setErrors] = useState<Error[]>([]);

  const addError = useCallback((message: string, source?: string) => {
    console.log('adding error', message, source);

    const newError: Error = {
      id: Date.now().toString(),
      message,
      timestamp: Date.now(),
      source,
      isRead: false,
    };
    setErrors(prev => [...prev, newError]);
  }, []);

  const markAsRead = useCallback((id: string) => {
    setErrors(prev =>
      prev.map(error =>
        error.id === id ? { ...error, isRead: true } : error
      )
    );
  }, []);

  const removeError = useCallback((id: string) => {
    setErrors(prev => prev.filter(error => error.id !== id));
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const unreadCount = errors.filter(error => !error.isRead).length;

  return (
    <ErrorContext.Provider
      value={{
        errors,
        addError,
        markAsRead,
        removeError,
        clearAllErrors,
        unreadCount
      }}
    >
      {children}
    </ErrorContext.Provider>
  );
};
